import * as KendoChart from '@progress/kendo-react-charts'
import { DateRangePicker } from '@progress/kendo-react-dateinputs'
import { ExcelExport } from '@progress/kendo-react-excel-export'
import { Grid, GridCell, GridColumn } from '@progress/kendo-react-grid'
import { DropdownComponent } from 'components/dropdown/component'
import { HeaderComponent } from 'components/header/component'
import { AggregateIntervalOptions, DateRangesAsDropdownOptions, SuspectFilterDropdownOptions
} from 'elasticsearch/constants'
import { DateRanges, toFilter } from 'elasticsearch/constants'
import * as React from 'react'
import { Button } from 'reactstrap'
import * as Constants from 'components/analytics/constants'
import { RasaAnalyticsComponent } from 'components/analytics/rasa-analytics-component'
import './styles.scss'
import * as Utils from 'components/analytics/utils'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { PDFReportHeader } from 'components/analytics/PDFReportHeader'
import { Loading } from 'components/loading'
import { RasaContext } from 'context'
import { BlastsAPI } from './blasts-api'
import { SharedKeys, SharedStore } from 'shared/data-layer/sharedStore'
import { BillingPlanDetailCode } from 'shared_server_client/types/billing_plan'
import { BlastsFeatureUnavailable } from './blasts-feature-unavailable'

const initialState = {
  selectedDateRange: DateRangesAsDropdownOptions[4],
  selectedInterval: AggregateIntervalOptions[0],
  selectedBlast: { key: 'all', value: '', description: 'All Blasts' },
  blastsOptions: [{ key: 'all', value: '', description: 'All Blasts' }],
  blasts: [] as any[],
  hasFeatureAccess: false,
  isCheckingAccess: true,
}

export class BlastsAnalyticsComponent extends RasaAnalyticsComponent<any, any> {
  public static contextType = RasaContext

  constructor(props) {
    super(props, initialState)
    this.blastChanged = this.blastChanged.bind(this)
  }

  public componentDidMount() {
    super.componentDidMount()
    this.minCustomDateRange()
    this.sharedStore = SharedStore.instance(this.context)

    this.sharedStore.getValue(SharedKeys.activeCommunity).then((activeCommunity) => {
      const avlFeatures: BillingPlanDetailCode[] = activeCommunity.billingInfo.currentPlan.features || []
      const hasAccess = avlFeatures.indexOf(BillingPlanDetailCode.BLASTS) > -1

      this.setState({
        hasFeatureAccess: hasAccess,
        isCheckingAccess: false,
      })

      if (hasAccess) {
        const accountGuid = (activeCommunity.data || {}).community_account_guid
        const identityCommunityGuid = activeCommunity.data.identity_community_guid
        this.fetchBlastsForDropdown(accountGuid, identityCommunityGuid)
      }
    })
  }

  private fetchBlastsForDropdown = (accountGuid: string, identityCommunityGuid: string) => {
    BlastsAPI.fetchListsAndBlasts(accountGuid)
      .then(([lists, blasts]) => {
        const newsletterList = lists.find((list: any) =>
          list.options && list.options.newsletter_guid === identityCommunityGuid &&
          list.options.source_type === 'newsletter'
        )

        let filteredBlasts = blasts
        let options

        if (!newsletterList) {
          // If no newsletter list, just use all blasts
          options = [
            { key: 'all', value: '', description: 'All Blasts' },
            ...blasts.map((blast: any) => ({
              key: blast.id.toString(),
              value: blast.sequence_id ? blast.sequence_id.toString() : '',
              description: blast.name || `Blast ${blast.id}`
            }))
          ]
        } else {
          const now = new Date()
          filteredBlasts = blasts.filter((blast: any) => {
            if (!blast.lists || !blast.lists.some((list: any) => list.id === newsletterList.id)) {
              return false
            }
            if (!blast.start_date) {
              return false
            }
            return new Date(blast.start_date) < now
          })

          options = [
            { key: 'all', value: '', description: 'All Blasts' },
            ...filteredBlasts.map((blast: any) => ({
              key: blast.id.toString(),
              value: blast.sequence_id ? blast.sequence_id.toString() : '',
              description: blast.name || `Blast ${blast.id}`
            }))
          ]
        }

        this.setState({ blastsOptions: options, blasts: filteredBlasts })
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch blasts for dropdown:', error)
        this.setState({
          blastsOptions: [{ key: 'all', value: '', description: 'All Blasts' }]
        })
      })
  }

  public dateChanged = (e: any) => {
    this.setQuery('selectedDateRange', e.selected.value).then(() => {
      this.setState({
        selectedDateRange: e.selected,
      }, () => {
        this.setState({ isFilterLoaded: true })
      })
    })
  }

  private blastChanged = (e: any) => {
    const selectedBlast = this.state.blastsOptions.find((blast: any) => blast.key === e.selected.key)
    if (selectedBlast) {
      this.setState({ selectedBlast }, () => {
        this.setState({ isFilterLoaded: true })
      })
    }
  }

  public render() {
    if (this.state.isCheckingAccess) {
      return <Loading size="32" />
    }

    return(
      this.state.hasFeatureAccess ?
      <div className="analytics-component blasts-analytics-container">
        <HeaderComponent
          title={'BLASTS ANALYTICS'}
          subTitle={'Opens/Clicks'}
          description={[
            'Opens - The number of times your blast campaigns have been opened by your subscribers.',
            'Clicks - The number of times any of your subscribers have clicked on a link in your blast campaigns.',
            'Click to Open - the number of clicks out of the number of opens',
          ]}
        />
        <div className="dropdown-header">
          <div className="dropdown-inline">
            <DropdownComponent data={DateRangesAsDropdownOptions}
                                selected={this.state.selectedDateRange.key}
                                onChange={this.dateChanged}/>
          </div>
          <div className="dropdown-inline">
            <DropdownComponent data={SuspectFilterDropdownOptions}
                               selected={this.state.selectedSuspectClick.key}
                               onChange={this.suspectedClickChanged}/>
          </div>
          <div className="dropdown-inline">
            <DropdownComponent data={this.state.blastsOptions}
                               selected={this.state.selectedBlast.key}
                               onChange={this.blastChanged}/>
          </div>
        </div>
        <div className="date-range-picker">
        {this.state.selectedDateRange.key === '7' ?
        <DateRangePicker min={this.state.minCustomDateRange}
                          max ={new Date()}
                          onChange={this.createCustomDate} /> : null}
        </div>
        {this.state.isFilterLoaded &&
        <BlastsAnalyticsEngagementComponent
          dateRange={this.state.selectedDateRange.value}
          dateRangeLabel={this.state.selectedDateRange.description}
          timezone={this.state.timezone}
          useTimezoneForAnalytics={this.state.useTimezoneForAnalytics}
          selectedInterval={this.state.selectedInterval.value}
          suspectClick={this.state.selectedSuspectClick.value}
          timestampLabel={this.timestampLabel}
          selectedBlastId={this.state.selectedBlast.value}
          blasts={this.state.blasts}
        />}
      </div> :
      <BlastsFeatureUnavailable />
    )
  }
}

const uniqueOpenColor = '#004f6d'
const uniqueClickColor = '#4ecdc4'
const uniqueClickToOpenColor = '#ff6b6b'

interface Point {
  messageId: number,
  blastName: string,
  totalClick: number,
  totalClickRate: number,
  totalDelivered: number,
  totalOpen: number,
  totalOpenRate: number,
  uniqueClick: number,
  uniqueClickRate: number,
  uniqueDelivered: number,
  uniqueOpen: number,
  uniqueOpenRate: number,
  uniqueClickToOpenRate?: number,
  suspectClick?: number,
}

type Points = Point[]

interface EngagementProps {
  dateRange: string,
  selectedInterval: string,
  hideGrid?: boolean,
  hideMetricSelector?: boolean,
  suspectClick?: any
  timezone?: string,
  useTimezoneForAnalytics?: any,
  timestampLabel?: (dateNumber: string | number, dateString: string) => string
  dateRangeLabel?: string
  selectedBlastId?: string
  blasts?: any[]
}

interface MetricConfig {
  key: string,
  label: string,
  field: string,
  color: string,
  axis: string,
  selected: boolean,
}

interface EngagementState {
  loaded: boolean,
  displayLabel: boolean,
  availableMetrics: MetricConfig[],
  selectedMetrics: MetricConfig[],
  isLoadingData: boolean,
  results: Points,
}

class BlastsAnalyticsEngagementClass extends React.Component<EngagementProps, EngagementState> {
  private xlsxExport: any = null
  private pdfExportRef: React.RefObject<HTMLDivElement> = React.createRef()
  private accountGuid: string = ''

  constructor(p: EngagementProps) {
    super(p)
    const availableMetrics: MetricConfig[] = [
      { key: 'uniqueClick', label: 'Unique Click', field: 'uniqueClick', color: uniqueClickColor, axis: 'left' as const, selected: true },
      { key: 'clickToOpen', label: 'Click To Open', field: 'uniqueClickToOpenRate', color: uniqueClickToOpenColor, axis: 'right' as const, selected: true },
      { key: 'uniqueOpen', label: 'Unique Open', field: 'uniqueOpen', color: uniqueOpenColor, axis: 'left' as const, selected: true },
      { key: 'totalClick', label: 'Total Click', field: 'totalClick', color: '#ff9999', axis: 'left' as const, selected: false },
      { key: 'totalOpen', label: 'Total Open', field: 'totalOpen', color: '#99ccff', axis: 'left' as const, selected: false },
      { key: 'delivered', label: 'Delivered', field: 'uniqueDelivered', color: '#90EE90', axis: 'left' as const, selected: true },
    ]
    this.state = {
      loaded: false,
      displayLabel: true,
      availableMetrics,
      selectedMetrics: availableMetrics.filter(m => m.selected),
      isLoadingData: false,
      results: [],
    }
  }

  public static contextType = RasaContext
  public context!: React.ContextType<typeof RasaContext>

  public componentDidMount() {
    this.context.user.init().then(({ activeCommunity }) => {
      this.accountGuid = (activeCommunity.data || {}).community_account_guid
      this.fetchAnalytics()
    })
  }

  private fetchAnalytics = () => {
    if (!this.accountGuid) {
      return
    }

    this.setState({ isLoadingData: true })

    const dateRange = toFilter(this.props.dateRange || DateRanges.PastMonth, this.props.timezone, this.props.useTimezoneForAnalytics)

    const query: any = {
      bool: {
        must: [
          {
            range: {
              created: {
                gte: dateRange.gte,
                lte: dateRange.lt
              }
            }
          }
        ]
      }
    }

    if (this.props.suspectClick && Utils.isRealClickSelected(this.props.suspectClick)) {
      query.bool.must.push({ term: { suspect_click: 0 } })
    }

    if (this.props.selectedBlastId && this.props.selectedBlastId !== '') {
      query.bool.must.push({ term: { sequence_id: parseInt(this.props.selectedBlastId, 10) } })
    }

    const payload = {
      query,
      aggregation: {
        by_send: {
          terms: {
            field: 'send_id',
            size: 1000
          },
          aggs: {
            message_id: {
              terms: {
                field: 'message_id',
                size: 1
              }
            },
            send_date: {
              min: {
                field: 'created'
              }
            },
            send_count: {
              filter: {
                term: {
                  event_name: 'delivered'
                }
              }
            },
            unique_send_count: {
              filter: {
                term: {
                  event_name: 'delivered'
                }
              },
              aggs: {
                unique_subscribers: {
                  cardinality: {
                    field: 'subscriber_id'
                  }
                }
              }
            },
            open_count: {
              filter: {
                term: {
                  event_name: 'open'
                }
              }
            },
            unique_open_count: {
              filter: {
                term: {
                  event_name: 'open'
                }
              },
              aggs: {
                unique_subscribers: {
                  cardinality: {
                    field: 'subscriber_id'
                  }
                }
              }
            },
            click_count: {
              filter: {
                term: {
                  event_name: 'click'
                }
              }
            },
            unique_click_count: {
              filter: {
                term: {
                  event_name: 'click'
                }
              },
              aggs: {
                unique_subscribers: {
                  cardinality: {
                    field: 'subscriber_id'
                  }
                }
              }
            }
          }
        }
      },
      limit: 0,
      skipReportsFilter: false
    }

    const apiCall = BlastsAPI.fetchAnalytics(this.accountGuid, payload)

    apiCall
      .then((response) => {
        const results = this.parseResponse(response)
        this.setState({
          results,
          loaded: true,
          isLoadingData: false
        })
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch analytics:', error)
        this.setState({
          loaded: true,
          isLoadingData: false,
          results: []
        })
      })
  }

  public parseResponse(response: any): Points {
    if (!response || !response.aggregations || !response.aggregations.by_send || !response.aggregations.by_send.buckets) {
      return []
    }

    const buckets = response.aggregations.by_send.buckets

    // Create a mapping from message_id (blast.id) to blast name
    const messageIdToBlastName: { [key: number]: string } = {}
    if (this.props.blasts && this.props.blasts.length > 0) {
      this.props.blasts.forEach((blast: any) => {
        if (blast.id) {
          messageIdToBlastName[blast.id] = blast.name || `Blast ${blast.id}`
        }
      })
    }

    // Group by message_id and aggregate metrics
    const groupedByMessage: any = {}

    buckets.forEach((bucket: any) => {
      const messageId = bucket.message_id && bucket.message_id.buckets && bucket.message_id.buckets.length > 0
        ? bucket.message_id.buckets[0].key
        : null

      if (!messageId) return

      if (!groupedByMessage[messageId]) {
        groupedByMessage[messageId] = {
          messageId,
          blastName: messageIdToBlastName[messageId] || `Blast ${messageId}`,
          totalClick: 0,
          totalOpen: 0,
          totalDelivered: 0,
          uniqueClick: 0,
          uniqueOpen: 0,
          uniqueDelivered: 0,
        }
      }

      // Aggregate metrics
      groupedByMessage[messageId].totalClick += bucket.click_count ? bucket.click_count.doc_count : 0
      groupedByMessage[messageId].totalOpen += bucket.open_count ? bucket.open_count.doc_count : 0
      groupedByMessage[messageId].totalDelivered += bucket.send_count ? bucket.send_count.doc_count : 0
      groupedByMessage[messageId].uniqueClick += bucket.unique_click_count && bucket.unique_click_count.unique_subscribers ? bucket.unique_click_count.unique_subscribers.value : 0
      groupedByMessage[messageId].uniqueOpen += bucket.unique_open_count && bucket.unique_open_count.unique_subscribers ? bucket.unique_open_count.unique_subscribers.value : 0
      groupedByMessage[messageId].uniqueDelivered += bucket.unique_send_count && bucket.unique_send_count.unique_subscribers ? bucket.unique_send_count.unique_subscribers.value : 0
    })

    // Convert to array and calculate rates
    return Object.values(groupedByMessage).map((partial: any) => {
      return {
        ...partial,
        totalClickRate: (
          (partial.totalDelivered > 0 ?
            partial.totalClick / partial.totalDelivered : 0) * 100.0).toFixed(2),
        totalOpenRate: (
          (partial.totalDelivered > 0 ?
            partial.totalOpen / partial.totalDelivered : 0) * 100.0).toFixed(2),
        uniqueClickRate: (
          (partial.uniqueDelivered > 0 ?
            partial.uniqueClick / partial.uniqueDelivered : 0) * 100.0).toFixed(2),
        uniqueOpenRate: (
          (partial.uniqueDelivered > 0 ?
            partial.uniqueOpen / partial.uniqueDelivered : 0) * 100.0).toFixed(2),
        uniqueClickToOpenRate: (
          (partial.uniqueClick > 0 && partial.uniqueOpen > 0 ?
            partial.uniqueClick / partial.uniqueOpen : 0) * 100.0).toFixed(2),
      }
    })
  }

  public componentDidUpdate(oldProps: EngagementProps) {
    if ( this.props.dateRange !== oldProps.dateRange  ||
         this.props.suspectClick !== oldProps.suspectClick ||
         this.props.selectedInterval !== oldProps.selectedInterval ||
         this.props.selectedBlastId !== oldProps.selectedBlastId ) {
      this.fetchAnalytics()
    }
  }

  public tooMuchData = () => {
    const display = (this.state.results && this.state.results.length < Constants.INTERVAL_THRESHOLD)
    this.setState({displayLabel: !display})
  }

  public toggleMetric = (metricKey: string) => {
    const updatedMetrics = this.state.availableMetrics.map(metric =>
      metric.key === metricKey ? { ...metric, selected: !metric.selected } : metric
    )
    this.setState({
      availableMetrics: updatedMetrics,
      selectedMetrics: updatedMetrics.filter(m => m.selected)
    })
  }

  public toggleAxis = (metricKey: string) => {
    const updatedMetrics = this.state.availableMetrics.map(metric =>
      metric.key === metricKey ? {
        ...metric,
        axis: (metric.axis === 'left' ? 'right' : 'left')
       } : metric
    )
    this.setState({
      availableMetrics: updatedMetrics,
      selectedMetrics: updatedMetrics.filter(m => m.selected)
    })
  }

  public exportToPDF = async () => {
    if (!this.pdfExportRef.current) return

    try {
      const element = this.pdfExportRef.current

      element.classList.add('pdf-export-mode')

      await new Promise(resolve => setTimeout(resolve, 100))

      const canvas = await html2canvas(element, {
        height: element.scrollHeight,
        width: element.scrollWidth,
        useCORS: true,
        scale: 2
      })

      element.classList.remove('pdf-export-mode')

      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF('l', 'mm', 'a4')

      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()
      const imgWidth = canvas.width
      const imgHeight = canvas.height
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight)
      const imgX = (pdfWidth - imgWidth * ratio) / 2
      const imgY = 10

      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio)
      pdf.save('blasts-opens-clicks-report.pdf')
    } catch (error) {
      if (this.pdfExportRef.current) {
        this.pdfExportRef.current.classList.remove('pdf-export-mode')
      }
    }
  }

  public render = () => <div className="analytics">
    {!this.props.hideMetricSelector && (
      <div className="metric-selector-compact">
        <h4>Metrics:</h4>
        <div className="metric-grid">
          {this.state.availableMetrics.map(metric => (
            <div key={metric.key} className="metric-chip">
              <label className="metric-label">
                <input
                  type="checkbox"
                  checked={metric.selected}
                  onChange={() => this.toggleMetric(metric.key)}
                />
                <span className="metric-color-dot" style={{ backgroundColor: metric.color }}></span>
                <span className="metric-text">{metric.label}</span>
              </label>
            </div>
          ))}
        </div>
      </div>
    )}
    <div className="engagement-chart">
      {this.state.isLoadingData ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Loading size="32" />
        </div>
      ) : (this.state.results && this.state.results.length > 0 ?
        <div>
          <div ref={this.pdfExportRef} className="pdf-report-container">
            <PDFReportHeader
              title="Blasts Opens & Clicks Report"
              dateRange={this.props.dateRange}
              dateRangeLabel={this.props.dateRangeLabel}
              fields={[
                { label: 'Data Grouping', value: this.props.selectedInterval },
                { label: 'Selected Metrics', value: this.state.selectedMetrics.map(m => m.label).join(', ') },
              ]}
            />
            <div className="pdf-chart-section">
              <h3>Performance Chart</h3>
              <KendoChart.Chart transitions={false} pannable={false} zoomable={false}>
          <KendoChart.ChartLegend position="bottom" orientation="horizontal"/>
          <KendoChart.ChartTooltip
            render={({points}: KendoChart.SharedTooltipContext) => <div className="engagement-tooltip">
              <div>
                {points[0].dataItem.blastName || ''}
              </div>
            {points.map((point) =>
              <div key={point.series.name + point.value} style={{color: point.series.color}}>
                {point.series.name}: {point.value}%
              </div>)}
            </div>}
            shared={true} />
            <KendoChart.ChartCategoryAxis>
              <KendoChart.ChartCategoryAxisItem
                categories={this.state.results.map((item) => item.blastName)}
                axisCrossingValue={this.state.selectedMetrics.some(m => m.axis === 'right') ? [0, 999999] : [0]}/>
            </KendoChart.ChartCategoryAxis>
            <KendoChart.ChartValueAxis>
              <KendoChart.ChartValueAxisItem name="leftAxis" title={{ text: "Count" }} />
              {this.state.selectedMetrics.some(m => m.axis === 'right') &&
                <KendoChart.ChartValueAxisItem name="rightAxis" title={{ text: "Percentage (%)" }} />
              }
            </KendoChart.ChartValueAxis>
            <KendoChart.ChartSeries>
              {this.state.selectedMetrics.map(metric => (
                <KendoChart.ChartSeriesItem
                  key={metric.key}
                  type="column"
                  color={metric.color}
                  field={metric.field}
                  name={metric.label}
                  data={this.state.results}
                  axis={metric.axis === 'left' ? 'leftAxis' : 'rightAxis'}>
                    {this.state.results && this.state.results.length < Constants.INTERVAL_THRESHOLD &&
                    <KendoChart.ChartSeriesLabels format={metric.axis === 'left' ? "{0:N0}" : "{0:N2}%"} />}
                </KendoChart.ChartSeriesItem>
              ))}
            </KendoChart.ChartSeries>
              </KendoChart.Chart>
            </div>
            <div className="pdf-table-section">
              <h3>Detailed Data</h3>
              <Grid data={this.state.results} className="analytics-counts-grid pdf-table" scrollable="none">
                <GridColumn field="blastName" title="Blast"/>
                <GridColumn field="totalOpen" title="Total Opens"/>
                <GridColumn field="totalClick" title="Total Clicks"/>
                <GridColumn field="uniqueOpen" title="Unique Opens"/>
                <GridColumn field="uniqueClick" title="Unique Clicks"/>
                <GridColumn field="uniqueClickToOpenRate" title="Clicks To Open" cell={ClickToOpenCell}/>
                <GridColumn field="totalDelivered" title="Delivered"/>
              </Grid>
            </div>
          </div>
          {!this.props.hideGrid &&
          <div className="opens-clicks">
            <Button
              disabled={this.state.results.length < 1 || this.state.isLoadingData}
              onClick={() => this.xlsxExport.save()}
              style={{ marginRight: '10px' }}>
              Export xlsx
            </Button>
            <Button
              disabled={this.state.results.length < 1 || this.state.isLoadingData}
              onClick={this.exportToPDF}
              color="secondary">
              Export PDF
            </Button>
          <ExcelExport data={this.state.results}
            fileName="BlastsAnalyticsReport.xlsx"
            ref={(exporter) => {this.xlsxExport = exporter}}>
            <Grid data={this.state.results} className="analytics-counts-grid" scrollable="none">
              <GridColumn field="blastName" title="Blast"/>
              <GridColumn field="totalOpen" title="Total Opens"/>
              <GridColumn field="totalClick" title="Total Clicks"/>
              <GridColumn field="uniqueOpen" title="Unique Opens"/>
              <GridColumn field="uniqueClick" title="Unique Clicks"/>
              <GridColumn field="uniqueClickToOpenRate" title="Clicks To Open" cell={ClickToOpenCell}/>
              <GridColumn field="totalDelivered" title="Delivered"/>
            </Grid>
          </ExcelExport>
          </div>}
        </div> :
        <div>
          <p className="no-data-tag">
            {Constants.NO_DATA_COPY}
          </p>
      </div>)}
      </div>
  </div>

}

class ClickToOpenCell extends GridCell {
  public render() {
    const d = this.props.dataItem.uniqueClickToOpenRate
    return <td>
      <span>{d}%</span>
    </td>
  }
}

export const BlastsAnalyticsEngagementComponent = BlastsAnalyticsEngagementClass
